import { useWallet } from "@solomon-apps/shared";
import { useEffect, useState } from "react";

const STORAGE_KEY_LAST_ADDRESS = "solomon:lastWalletAddress";

/** Max wait for the wallet adapter to produce a `connecting`/`connected` signal
 *  during an auto-restore on fresh mount. Long enough to cover real-world
 *  restore latency, short enough that non-restoring users don't stall. */
const WALLET_RESTORE_GRACE_MS = 800;

function hasWalletHistory(): boolean {
    if (typeof window === "undefined") return false;
    try {
        return window.localStorage.getItem(STORAGE_KEY_LAST_ADDRESS) !== null;
    } catch {
        return false;
    }
}

/**
 * Wallet-restore grace window for returning users.
 *
 * On a fresh mount for a user who connected before, the wallet adapter takes
 * tens to a few hundred ms to begin restoring — during that gap `connected`
 * and `connecting` are both `false`, which would otherwise flash the
 * logged-out UI. This hook returns `false` during the grace window when
 * previous wallet history exists, and flips to `true` the moment the adapter
 * produces a signal OR the grace timer expires.
 *
 * Users with no wallet history skip the grace entirely (returns `true`
 * synchronously from first render).
 */
export function useWalletRestoreGrace(): boolean {
    const { connected, connecting } = useWallet();

    const [settled, setSettled] = useState<boolean>(() => connected || connecting || !hasWalletHistory());

    useEffect(() => {
        if (settled) return;
        if (connected || connecting) {
            setSettled(true);
            return;
        }
        const t = setTimeout(() => setSettled(true), WALLET_RESTORE_GRACE_MS);
        return (): void => clearTimeout(t);
    }, [settled, connected, connecting]);

    return settled;
}
